/*
/*
 * @Author: nhsoft.wh
 * @Date: 2021-07-12 16:57:30
 * @LastEditors: 赵泽文
 * @LastEditTime: 2026-03-31 17:45:03
 * @Description: file content
 */

import { message, Modal } from '@lemon-fe/kits';
import { nanoid } from 'nanoid';

import type { IGetClientProps } from './multipleDomain';
import {
  getCurrentTokenLayer,
  getToperDomain,
  handleTokenFresh,
  logout,
  autoSwitchHostNameWhenDebug,
} from './multipleDomain';
import type { IClientConfig } from 'mars-front-api-sdk/types/Client';
import { sessionCache, localCache } from './storageHandler';
import errorMessage from './error-message';
import { $t } from './intl';

import appJSON from '../../app.json';

let isLoginExpiredModalVisible = false;

export const showLoginExpiredModal = () => {
  if (isLoginExpiredModalVisible) {
    return;
  }

  isLoginExpiredModalVisible = true;

  Modal.confirm({
    title: $t({ defaultMessage: '账号验证已过期，请重新登录' }),
    okText: $t({ defaultMessage: '返回登录' }),
    cancelButtonProps: { style: { display: 'none' } },
    centered: true,
    onOk: () => {
      isLoginExpiredModalVisible = false;
      window.logoutAndRedirect?.({ noTips: true });
    },
    onCancel: () => {
      isLoginExpiredModalVisible = false;
    },
  });
};

export const getCookie = (name: string) => {
  // operator_id, user_operator优先从sessionStorage中获取
  const userSessionSet = sessionCache.getItem('user_session_set');
  const sessionMerchantId = sessionCache.getItem('merchant_id');
  if (userSessionSet && sessionMerchantId && ['operator_id', 'user_operator'].includes(name)) {
    const value = sessionCache.getItem([sessionMerchantId, name].join('_'));
    if (value) {
      return value;
    }
  }

  if (sessionCache.getItem(name)) {
    return sessionCache.getItem(name);
  }
  const cookieArr = document.cookie.split('; ');

  // 可能存在同名cookie，优先取最近修改的
  const results: string[] = [];
  for (let i = 0; i < cookieArr.length; i += 1) {
    const cookieArrArr = cookieArr[i].split('=');
    if (cookieArrArr[0] === name) {
      results.push(unescape(cookieArrArr[1]));
    }
  }

  if (results?.length) {
    return results[results.length - 1];
  }

  return '';
};

export const getOriginCookie = (name: string) => {
  const cookieArr = document?.cookie?.split('; ');

  // 可能存在同名cookie，优先取最近修改的
  const results: string[] = [];
  for (let i = 0; i < cookieArr?.length; i += 1) {
    const cookieArrArr = cookieArr[i].split('=');
    if (cookieArrArr[0] === name) {
      results.push(unescape(cookieArrArr[1]));
    }
  }

  if (results?.length) {
    return results[results.length - 1];
  }

  return '';
};

/**
 * 设置cookie
 * @description 注意：因为主项目进销存缓存存储方式不同，项目合并前，主项目使用cookie，进销存使用localstorage，注意区分location_id的不同
 * @description localstorage中的location_id在总部进入时显示为总部门店id，区域进入显示区域下第一家门店id，门店进入显示门店id；mars_location_id的数据同cookie中的location_id
 * @description cookie中的location_id在总部或区域进入显示总部或区域下第一家门店id，门店进入显示门店id
 */
export const setCookie = (name: string, value?: string, day?: number, domain?: string) => {
  let cookieStr = `${name}=${escape(value || '')}`;
  const date = new Date();
  const deadline = (day || 7) * 24 * 3600 * 1000;
  date.setTime(date.getTime() + deadline);
  cookieStr = `${cookieStr}; expires=${date.toUTCString()}; path=${escape('/')}; domain=${
    domain || window.location.hostname
  }`;
  document.cookie = cookieStr;
  sessionCache.setItem(name, value || '');
};

/**
 * 商户商户存在localStorage中的token
 */
const deleteMerchantLocalStorageToken = () => {
  const merchant_id = getCookie('merchant_id');

  localStorage.removeItem(`${merchant_id}_access_token`);
  localStorage.removeItem(`${merchant_id}_refresh_token`);
};

export const delAllCookie = (args?: { excludeKeys?: string[] }) => {
  // 删除cookie的同时，删除存在storage中的商户token
  deleteMerchantLocalStorageToken();
  sessionStorage.clear();
  const expireDate = new Date();
  expireDate.setTime(-1000);
  const allCookie = document.cookie;
  const allCookieArray = allCookie.split('; ');

  for (let i = 0; i < allCookieArray.length; i += 1) {
    const varName = allCookieArray[i].split('=');
    if (args?.excludeKeys?.includes(varName[0])) {
      continue;
    }

    sessionCache.removeItem(`${varName[0]}`);
    document.cookie = `${varName[0]}=''; expires=${expireDate.toUTCString()}; path=${escape(
      '/',
    )}; domain=${window.location.hostname}`;
    document.cookie = `${varName[0]}=''; expires=${expireDate.toUTCString()}; path=${escape(
      '/',
    )}; domain=${getToperDomain()}`;
  }
};

export interface IErrorArray {
  field: string;
  message: string;
}
export interface IClientError {
  code: number;
  errors: string | IErrorArray[];
}

// 不需要拦截器弹出错误信息的白名单
const notPopUpErrorMessageCodeWhitelist = [1000029, 3000026];

const clientErrorMap: Record<string, string> = {
  'Not Authenticated': '登陆失效',
};

export const clientErrorResolve = (
  res: IClientError,
  status?: string | number,
  traceId?: string,
) => {
  if (res && res.errors) {
    if (res.code === 3000023) {
      logoutAndRedirect();
      return;
    }
    // code是否在无需提示的白名单中
    if (res.code && notPopUpErrorMessageCodeWhitelist.includes(res.code)) {
      return;
    }
    if (Array.isArray(res.errors)) {
      message.error(
        errorMessage({
          msg: res.errors[0].message || window.$t({ defaultMessage: '网络异常，请稍后再试' }),
          traceId,
        }),
      );
    } else if ((typeof res.errors).toLowerCase() === 'string') {
      const error = clientErrorMap[res.errors] || res.errors;
      if (error === '登陆失效') {
        message.error(errorMessage({ msg: error, traceId }));
      } else {
        message.error(
          errorMessage({
            msg: error || window.$t({ defaultMessage: '网络异常，请稍后再试' }),
            traceId,
          }),
        );
      }
    }
  } else {
    message.error(
      errorMessage({ msg: window.$t({ defaultMessage: '网络异常，请稍后再试' }), traceId }),
    );
  }
};

export const logoutAndRedirect = async (options?: { noTips?: boolean }) => {
  if (!options?.noTips) {
    message.error($t({ defaultMessage: '登录失效，请重新登录' }));
  }

  logout();
  localStorage.removeItem('showNoticeUpdateModal');
  await window.marsClient.account.login.logout();

  window.location.href = `${window.location.protocol}//${autoSwitchHostNameWhenDebug(
    `account${getToperDomain()}`,
  )}:${window.location.port}/login`;
};

/** 判断当前设备是否为移动端 还有是否X11，类linux系统 */
export const isMobilePhone = () => {
  return /Mobi|Android|iPhone|iPad|X11/i.test(navigator.userAgent);
};

export const getCookieHeader = () => {
  const layer = getCurrentTokenLayer();
  const merchantId = getCookie('merchant_id');
  // 可能刚刚进行完商户登录，cookie还没转移到localStorage
  // 旧的商户的token有没有删除的可能性，需要先到cookie中寻找
  // 如果cookie中有，说明还没转移，指定错不了
  // 如果cookie中没有，说明转移过了，读localStorage中的，指定错不了
  const accessToken =
    layer === 'LOGIN'
      ? getCookie('access_token')
      : getCookie(`${merchantId}_access_token`) ||
        localStorage.getItem(`${merchantId}_access_token`);
  const refreshToken =
    layer === 'LOGIN'
      ? getCookie('refresh_token')
      : getCookie(`${merchantId}_refresh_token`) ||
        localStorage.getItem(`${merchantId}_refresh_token`);

  const operator = getCookie('user_operator');
  let deviceId = getCookie('DEVICE_ID');

  if (!deviceId) {
    deviceId = nanoid(32);

    setCookie('DEVICE_ID', deviceId, 365, getToperDomain());
  }

  let client_id = window.MARS_BASE_CONFIG.CLIENT_ID;
  let client_secret = window.MARS_BASE_CONFIG.CLIENT_SECRET;

  if (layer === 'MERCHANT') {
    client_id = window.MARS_BASE_CONFIG.MERCHANT_CLIENT_ID;
    client_secret = window.MARS_BASE_CONFIG.MERCHANT_CLIENT_SECRET;
  }

  const header: IGetClientProps = {
    merchantId,
    accessToken,
    refreshToken,
    operator,
    deviceId,
    client_id,
    client_secret,
  };

  if (layer === 'MERCHANT') {
    header.grant_type = 'account_access_token';
  }

  return header;
};

export const getClient = ({
  accessToken,
  refreshToken,
  merchantId,
  operator,
  deviceId,
  grant_type,
  client_id,
  client_secret,
  ...props
}: IGetClientProps) => {
  const finalRefreshToken = refreshToken || getCookie('refresh_token');

  const currentLanguage = localStorage.getItem('MarsLanguage');

  const clientConfig: IClientConfig = {
    header: {
      baseUrl: window.MARS_BASE_CONFIG.MARS_BASE_URL,
      refreshToken: finalRefreshToken,
      client_id: client_id || window.MARS_BASE_CONFIG.CLIENT_ID,
      client_secret: client_secret || window.MARS_BASE_CONFIG.CLIENT_SECRET,
      operator,
      accessToken,
      merchantId,
      deviceId,
      source: 'WEB_ADMIN',
      grant_type,
      version: appJSON.dist,
      cb: handleTokenFresh,
      acceptLanguage: currentLanguage?.includes('en') ? 'en_US' : 'zh_CN',
      onAuthExpired: showLoginExpiredModal,
      ...props,
    },
    fail: clientErrorResolve,
  };

  if (ModularizedLocalHost && Object.keys(ModularizedLocalHost)?.length) {
    clientConfig.host = ModularizedLocalHost;
  }
  // #endregion
  window.marsClient = window.makeClient(clientConfig);
};

export const getMarsStockStorage = () => {
  const merchantId = window.getCookie('merchant_id');
  const accountId = window.getCookie('account_id');
  const locationZoneId = window.getCookie('location_zone_id');
  const locationZoneName = window.getCookie('location_zone_name');
  const lastestLocationId = window.getCookie('location_id');
  const locationName = window.getCookie('locationName');
  const headerId = window.getCookie('header_id');
  const headerLocationName = window.getCookie('header_name');
  const isHeader = window.getCookie('headquarters_is') === '1';
  const isDistribution = window.getCookie('distribution_center_is') === '1';

  const firstLocationChainStatus = window.getCookie('firstLocationChainStatus');

  if (!merchantId || !accountId) {
    return;
  }

  localCache.setItem('merchant_id', merchantId);
  localCache.setItem('account_id', accountId);
  /** 总部或区域取下属第一家门店，门店取自己id */
  localCache.setItem('mars_location_id', lastestLocationId);
  localCache.setItem('mars_location_name', locationName);
  /** 第一家门店进销存模块过期状态 */
  localCache.setItem('firstLocationChainStatus', firstLocationChainStatus);
  /** 总部取总部门店id，区域取下属第一家门店，门店取自己id */
  localCache.setItem('location_id', isHeader ? headerId : lastestLocationId);
  localCache.setItem('location_name', isHeader ? headerLocationName : locationName);

  localCache.setItem('distribution_center_is', isDistribution ? '1' : '0');
  localCache.setItem('headquarters_is', isHeader ? '1' : '0');
  localCache.setItem('zone_is', !isHeader && locationZoneId ? '1' : '0');

  if (!locationZoneId) {
    // 没有区域id时即为门店，去除区域id显示
    localCache.removeItem('location_zone_id');
    localCache.removeItem('location_zone_name');
  } else {
    localCache.setItem('location_zone_id', locationZoneId);
    localCache.setItem('location_zone_name', locationZoneName);
  }
};
